import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  Link
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { formatDistanceToNow } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { notificationService } from '../../services/notification.service';

const NotificationCenter = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Use the notification service instead of direct fetch
      const data = await notificationService.getNotifications();
      setNotifications(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every minute
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatTopic = (topic) => {
    if (!topic) return '';
    const parts = topic.split('.');
    return parts[parts.length - 1].replace(/_/g, ' ').toUpperCase();
  };

  const isProductRecall = (topic) => {
    if (!topic) return false;
    return topic.includes('PRODUCT_SAFETY_RECALL');
  };

  const unreadCount = notifications ? notifications.filter(n => !n.read).length : 0;
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          '& .MuiSvgIcon-root': {
            filter: 'brightness(2)',
          }
        }}
      >
        <Badge 
          badgeContent={notifications ? notifications.length : 0} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4444',
              color: 'white'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: 400,
            maxHeight: 500,
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box sx={{ width: 400, maxHeight: 500, overflow: 'auto' }}>
          <Typography sx={{ p: 2 }} variant="h6">Notifications</Typography>
          <List>
            {loading ? (
              <ListItem>
                <ListItemText primary="Loading notifications..." />
              </ListItem>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((notification) => {
                const isRecall = isProductRecall(notification.topic);
                
                return (
                  <ListItem 
                    key={notification.message_id} 
                    divider
                    sx={{ 
                      backgroundColor: notification.read ? 'inherit' : 
                        isRecall ? 'warning.main' : 'rgba(25, 118, 210, 0.08)',
                      '&:hover': {
                        backgroundColor: isRecall ? 'warning.dark' : 'rgba(25, 118, 210, 0.12)',
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography 
                            variant="subtitle1" 
                            component="span"
                            sx={{ 
                              color: isRecall ? '#fff' : 'text.primary',
                              fontWeight: isRecall ? 600 : 400
                            }}
                          >
                            {notification.filter_name}
                          </Typography>
                          <Chip 
                            label={formatTopic(notification.topic)} 
                            size="small" 
                            sx={{ 
                              height: 20,
                              fontWeight: isRecall ? 600 : 400,
                              backgroundColor: isRecall ? 'rgba(255, 255, 255, 0.9)' : undefined,
                              color: isRecall ? 'warning.dark' : undefined
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 0.5,
                              color: isRecall ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary'
                            }}
                          >
                            {formatDistanceToNow(new Date(notification.timestamp))} ago
                          </Typography>
                          
                          {notification.action && (
                            <Box sx={{ mt: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  color: isRecall ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary',
                                  fontWeight: isRecall ? 500 : 400
                                }}
                              >
                                • {notification.action.description}
                                {notification.action.url && (
                                  <RouterLink
                                    to={notification.action.url.replace('http://localhost:3000', '')}
                                    style={{ 
                                      color: isRecall ? '#fff' : '#1976d2',
                                      textDecoration: 'underline',
                                      fontWeight: 600
                                    }}
                                    onClick={handleClose}
                                  >
                                    View Details
                                  </RouterLink>
                                )}
                              </Typography>
                            </Box>
                          )}

                          {notification.notification_count > 1 && (
                            <Chip 
                              label={`+${notification.notification_count - 1} more updates`} 
                              size="small"
                              sx={{ 
                                mt: 1, 
                                height: 20,
                                fontWeight: isRecall ? 500 : 400,
                                backgroundColor: isRecall ? 'rgba(255, 255, 255, 0.9)' : undefined,
                                color: isRecall ? 'warning.dark' : undefined,
                                borderColor: isRecall ? 'rgba(255, 255, 255, 0.9)' : undefined
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })
            ) : (
              <ListItem>
                <ListItemText primary="No notifications" />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter; 